import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Check,
  X,
  Lock,
  Zap,
  Crown,
  Star,
  Loader2,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature comparison data - comprehensive list of all features
const FEATURE_COMPARISON = [
  { 
    category: "IP Intelligence",
    features: [
      { name: "IP Logging", free: true, basic: true, advanced: true, professional: true },
      { name: "Geolocation (Country, City, ISP)", free: true, basic: true, advanced: true, professional: true },
      { name: "Basic VPN Detection (~30%)", free: true, basic: true, advanced: true, professional: true },
      { name: "Enhanced VPN Detection (95%)", free: false, basic: true, advanced: true, professional: true },
      { name: "Proxy/Tor/Bot Detection", free: false, basic: true, advanced: true, professional: true },
      { name: "Fraud Risk Scoring", free: false, basic: true, advanced: true, professional: true },
    ]
  },
  {
    category: "Investigation Tools",
    features: [
      { name: "Email Header Analysis", free: "2/mo", basic: "20/mo", advanced: "100/mo", professional: "500/mo" },
      { name: "Email Reputation Check", free: "2/mo", basic: "20/mo", advanced: "100/mo", professional: "500/mo" },
      { name: "Phone Number Lookup", free: "2/mo", basic: "20/mo", advanced: "100/mo", professional: "500/mo" },
      { name: "Social Profile Search", free: false, basic: "20/mo", advanced: "100/mo", professional: "500/mo" },
    ]
  },
  {
    category: "Tracking & Analytics",
    features: [
      { name: "Link Click Tracking", free: true, basic: true, advanced: true, professional: true },
      { name: "Tracking Pixel", free: true, basic: true, advanced: true, professional: true },
      { name: "Real-time Dashboard", free: true, basic: true, advanced: true, professional: true },
      { name: "Visitor Analytics", free: true, basic: true, advanced: true, professional: true },
    ]
  },
  {
    category: "Data & Export",
    features: [
      { name: "IP Lookups", free: "2/mo", basic: "50/mo", advanced: "500/mo", professional: "2,500/mo" },
      { name: "Export to CSV", free: false, basic: true, advanced: true, professional: true },
      { name: "Export to JSON", free: false, basic: true, advanced: true, professional: true },
      { name: "Data Retention", free: "7 days", basic: "30 days", advanced: "90 days", professional: "1 year" },
    ]
  },
  {
    category: "Developer & Support",
    features: [
      { name: "API Access", free: false, basic: false, advanced: true, professional: true },
      { name: "Webhook Notifications", free: false, basic: false, advanced: true, professional: true },
      { name: "Priority Support", free: false, basic: false, advanced: false, professional: true },
      { name: "Custom Integrations", free: false, basic: false, advanced: false, professional: true },
    ]
  }
];

// Pricing at 50% of market rate
const PRICING = {
  free: { monthly: 0, annual: 0 },
  basic: { monthly: 9.99, annual: 99.99 },
  advanced: { monthly: 24.99, annual: 249.99 },
  professional: { monthly: 49.99, annual: 499.99 }
};

const SubscriptionPage = () => {
  const { getAuthHeader } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    fetchData();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [featuresRes, usageRes] = await Promise.all([
        axios.get(`${API}/subscription/features`, { headers: getAuthHeader() }),
        axios.get(`${API}/user/usage`, { headers: getAuthHeader() })
      ]);
      setSubscriptionData(featuresRes.data);
      setUsage(usageRes.data);
    } catch (error) {
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please contact support.");
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: getAuthHeader()
      });

      if (response.data.payment_status === "paid") {
        toast.success(response.data.message || "Subscription activated!");
        setCheckingPayment(false);
        fetchData();
        // Remove session_id from URL
        navigate("/subscription", { replace: true });
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setCheckingPayment(false);
        navigate("/subscription", { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleUpgrade = async (tier) => {
    setUpgrading(tier);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscription/checkout?tier=${tier}&origin_url=${encodeURIComponent(originUrl)}`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setUpgrading(null);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case "basic": return <Zap className="w-5 h-5" />;
      case "advanced": return <Star className="w-5 h-5" />;
      case "professional": return <Crown className="w-5 h-5" />;
      default: return null;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case "basic": return "text-blue-500 border-blue-500/30 bg-blue-500/10";
      case "advanced": return "text-purple-500 border-purple-500/30 bg-purple-500/10";
      case "professional": return "text-amber-500 border-amber-500/30 bg-amber-500/10";
      default: return "text-muted-foreground border-border";
    }
  };

  // Get feature display with locked styling
  const FeatureRow = ({ featureKey, featureName, tierRequired, currentTier }) => {
    const tierOrder = ["free", "basic", "advanced", "professional"];
    const currentIndex = tierOrder.indexOf(currentTier);
    const requiredIndex = tierOrder.indexOf(tierRequired);
    const isAvailable = currentIndex >= requiredIndex;
    
    // Determine which tier unlocks this feature
    const getLockedStyle = () => {
      if (isAvailable) return null;
      if (tierRequired === "basic") return "bg-blue-500/5 text-blue-500/50";
      if (tierRequired === "advanced") return "bg-purple-500/5 text-purple-500/50";
      if (tierRequired === "professional") return "bg-amber-500/5 text-amber-500/50";
      return "bg-muted/30 text-muted-foreground/50";
    };

    const lockedStyle = getLockedStyle();

    return (
      <div className={`flex items-center justify-between py-2 px-3 rounded ${lockedStyle || ""}`}>
        <div className="flex items-center gap-2">
          {isAvailable ? (
            <Check className="w-4 h-4 text-emerald-500" />
          ) : (
            <Lock className="w-4 h-4 opacity-50" />
          )}
          <span className={!isAvailable ? "opacity-50" : ""}>{featureName}</span>
        </div>
        {!isAvailable && (
          <Badge variant="outline" className={`text-[10px] ${getTierColor(tierRequired)}`}>
            {tierRequired}
          </Badge>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (checkingPayment) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
        <p className="text-lg font-medium">Verifying your payment...</p>
        <p className="text-sm text-muted-foreground">This may take a few moments</p>
      </div>
    );
  }

  const currentTier = subscriptionData?.current_tier || "free";
  const allTiers = subscriptionData?.all_tiers || {};
  const features = subscriptionData?.features || {};

  return (
    <div className="space-y-6" data-testid="subscription-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight">
          Subscription
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Manage your plan and see what features are available
        </p>
      </div>

      {/* Current Plan Card */}
      <Card className={`border-2 ${getTierColor(currentTier)}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getTierIcon(currentTier) || <Zap className="w-6 h-6 text-muted-foreground" />}
              <div>
                <p className="text-sm text-muted-foreground">Current Plan</p>
                <p className="text-2xl font-bold capitalize">{currentTier}</p>
              </div>
            </div>
            {currentTier !== "free" && (
              <Badge className={getTierColor(currentTier)}>Active</Badge>
            )}
          </div>
          
          {/* Usage Stats */}
          {usage && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Email Lookups</span>
                  <span>{usage.usage.email.used}/{usage.usage.email.limit}</span>
                </div>
                <Progress value={(usage.usage.email.used / Math.max(usage.usage.email.limit, 1)) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Phone Lookups</span>
                  <span>{usage.usage.phone.used}/{usage.usage.phone.limit}</span>
                </div>
                <Progress value={(usage.usage.phone.used / Math.max(usage.usage.phone.limit, 1)) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Social Lookups</span>
                  <span>{usage.usage.social.used}/{usage.usage.social.limit}</span>
                </div>
                <Progress value={(usage.usage.social.used / Math.max(usage.usage.social.limit, 1)) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>IP Lookups</span>
                  <span>{usage.usage.ip.used}/{usage.usage.ip.limit}</span>
                </div>
                <Progress value={(usage.usage.ip.used / Math.max(usage.usage.ip.limit, 1)) * 100} className="h-2" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Features List */}
      <Card className="bg-card/50 border-border">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Your Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {Object.entries(features).map(([key, feature]) => (
              <FeatureRow
                key={key}
                featureKey={key}
                featureName={feature.name}
                tierRequired={feature.tier_required}
                currentTier={currentTier}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            <span className="inline-block w-3 h-3 rounded bg-blue-500/10 mr-1"></span> Basic features
            <span className="inline-block w-3 h-3 rounded bg-purple-500/10 mx-1 ml-3"></span> Advanced features
            <span className="inline-block w-3 h-3 rounded bg-amber-500/10 mx-1 ml-3"></span> Professional features
          </p>
        </CardContent>
      </Card>

      {/* Pricing Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Upgrade Your Plan
          </h2>
          <Badge variant="outline" className="text-emerald-500 border-emerald-500/30">
            50% off market rate
          </Badge>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Basic */}
          <Card className={`border-2 transition-all hover:border-blue-500/50 ${currentTier === "basic" ? "border-blue-500" : "border-border"}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-blue-500">
                  <Zap className="w-5 h-5" />
                  <CardTitle className="text-lg">Basic</CardTitle>
                </div>
                {currentTier === "basic" && <Badge className="bg-blue-500/10 text-blue-500">Current</Badge>}
              </div>
              <div className="mt-2">
                <span className="text-3xl font-bold">${PRICING.basic.monthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-muted-foreground">or ${PRICING.basic.annual}/year (save 17%)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <p><strong>20</strong> Phone lookups/mo</p>
                <p><strong>20</strong> Social lookups/mo</p>
                <p><strong>20</strong> Email lookups/mo</p>
                <p><strong>50</strong> IP lookups/mo</p>
                <p className="text-emerald-500">✓ VPN Detection (95%)</p>
                <p className="text-emerald-500">✓ Export CSV/JSON</p>
              </div>
              <Button 
                className="w-full bg-blue-500 hover:bg-blue-600"
                disabled={currentTier === "basic" || upgrading === "basic"}
                onClick={() => handleUpgrade("basic")}
              >
                {upgrading === "basic" ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                 currentTier === "basic" ? "Current Plan" : "Upgrade to Basic"}
              </Button>
            </CardContent>
          </Card>

          {/* Advanced - Most Popular */}
          <Card className={`border-2 transition-all relative ${currentTier === "advanced" ? "border-purple-500" : "border-purple-500/50 hover:border-purple-500"}`}>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-purple-500 text-white">Most Popular</Badge>
            </div>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-purple-500">
                  <Star className="w-5 h-5" />
                  <CardTitle className="text-lg">Advanced</CardTitle>
                </div>
                {currentTier === "advanced" && <Badge className="bg-purple-500/10 text-purple-500">Current</Badge>}
              </div>
              <div className="mt-2">
                <span className="text-3xl font-bold">${PRICING.advanced.monthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-muted-foreground">or ${PRICING.advanced.annual}/year (save 17%)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <p><strong>100</strong> Phone lookups/mo</p>
                <p><strong>100</strong> Social lookups/mo</p>
                <p><strong>100</strong> Email lookups/mo</p>
                <p><strong>500</strong> IP lookups/mo</p>
                <p className="text-emerald-500">✓ Everything in Basic</p>
                <p className="text-emerald-500">✓ API Access</p>
              </div>
              <Button 
                className="w-full bg-purple-500 hover:bg-purple-600"
                disabled={currentTier === "advanced" || upgrading === "advanced"}
                onClick={() => handleUpgrade("advanced")}
              >
                {upgrading === "advanced" ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                 currentTier === "advanced" ? "Current Plan" : "Upgrade to Advanced"}
              </Button>
            </CardContent>
          </Card>

          {/* Professional */}
          <Card className={`border-2 transition-all hover:border-amber-500/50 ${currentTier === "professional" ? "border-amber-500" : "border-border"}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-amber-500">
                  <Crown className="w-5 h-5" />
                  <CardTitle className="text-lg">Professional</CardTitle>
                </div>
                {currentTier === "professional" && <Badge className="bg-amber-500/10 text-amber-500">Current</Badge>}
              </div>
              <div className="mt-2">
                <span className="text-3xl font-bold">${PRICING.professional.monthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-muted-foreground">or ${PRICING.professional.annual}/year (save 17%)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <p><strong>500</strong> Phone lookups/mo</p>
                <p><strong>500</strong> Social lookups/mo</p>
                <p><strong>500</strong> Email lookups/mo</p>
                <p><strong>2,500</strong> IP lookups/mo</p>
                <p className="text-emerald-500">✓ Everything in Advanced</p>
                <p className="text-emerald-500">✓ Priority Support</p>
              </div>
              <Button 
                className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                disabled={currentTier === "professional" || upgrading === "professional"}
                onClick={() => handleUpgrade("professional")}
              >
                {upgrading === "professional" ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                 currentTier === "professional" ? "Current Plan" : "Upgrade to Professional"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Feature Comparison Table */}
      <Card className="bg-card/50 border-border" data-testid="feature-comparison-table">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Complete Feature Comparison</CardTitle>
          <p className="text-sm text-muted-foreground">Compare all features across subscription tiers</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="w-[280px]">Feature</TableHead>
                  <TableHead className="text-center w-[120px]">
                    <div className="text-muted-foreground">Free</div>
                    <div className="text-xs text-muted-foreground/70">$0</div>
                  </TableHead>
                  <TableHead className="text-center w-[120px]">
                    <div className="text-blue-500">Basic</div>
                    <div className="text-xs text-blue-500/70">${PRICING.basic.monthly}/mo</div>
                  </TableHead>
                  <TableHead className="text-center w-[120px]">
                    <div className="text-purple-500">Advanced</div>
                    <div className="text-xs text-purple-500/70">${PRICING.advanced.monthly}/mo</div>
                  </TableHead>
                  <TableHead className="text-center w-[120px]">
                    <div className="text-amber-500">Professional</div>
                    <div className="text-xs text-amber-500/70">${PRICING.professional.monthly}/mo</div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {FEATURE_COMPARISON.map((category, categoryIndex) => (
                  <>
                    {/* Category Header */}
                    <TableRow key={`cat-${categoryIndex}`} className="bg-muted/30 border-border">
                      <TableCell colSpan={5} className="font-semibold text-sm py-2">
                        {category.category}
                      </TableCell>
                    </TableRow>
                    {/* Features */}
                    {category.features.map((feature, featureIndex) => (
                      <TableRow key={`feat-${categoryIndex}-${featureIndex}`} className="border-border">
                        <TableCell className="text-sm">{feature.name}</TableCell>
                        <TableCell className="text-center">
                          <FeatureCell value={feature.free} tier="free" currentTier={currentTier} />
                        </TableCell>
                        <TableCell className="text-center">
                          <FeatureCell value={feature.basic} tier="basic" currentTier={currentTier} />
                        </TableCell>
                        <TableCell className="text-center">
                          <FeatureCell value={feature.advanced} tier="advanced" currentTier={currentTier} />
                        </TableCell>
                        <TableCell className="text-center">
                          <FeatureCell value={feature.professional} tier="professional" currentTier={currentTier} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Check className="w-4 h-4 text-emerald-500" />
              <span>Included</span>
            </div>
            <div className="flex items-center gap-1">
              <X className="w-4 h-4 text-muted-foreground/50" />
              <span>Not available</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-emerald-500 font-medium">10/mo</span>
              <span>= Monthly limit</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper component for feature table cells
const FeatureCell = ({ value, tier, currentTier }) => {
  const tierOrder = ["free", "basic", "advanced", "professional"];
  const isCurrentOrHigher = tierOrder.indexOf(currentTier) >= tierOrder.indexOf(tier);
  
  if (value === true) {
    return <Check className={`w-4 h-4 mx-auto ${isCurrentOrHigher ? "text-emerald-500" : "text-emerald-500/50"}`} />;
  }
  if (value === false) {
    return <X className="w-4 h-4 mx-auto text-muted-foreground/30" />;
  }
  // It's a string (limit)
  return (
    <span className={`text-xs font-medium ${isCurrentOrHigher ? "text-emerald-500" : "text-muted-foreground"}`}>
      {value}
    </span>
  );
};

export default SubscriptionPage;
